/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store.type"
import { Drawer, Button } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { isEmpty } from "lodash"
import { drawerFooter } from "components/drawers/styles"
import { Review, ReviewUnReportableReason } from "types/api/menuFeedback.type"
import { actionCreator } from "store/store"
import { useTranslationsMap } from "common/utils/i18n"
import { ActionType } from "types/models/feedback"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import DrawerReviewStatus from "./FeedbackDrawerComponents/DrawerReviewStatus"
import DrawerReviewDetails from "./FeedbackDrawerComponents/DrawerReviewDetails"
import { DrawerReviewReplyForm } from "./FeedbackDrawerComponents/DrawerReviewReplyForm/DrawerReviewReplyForm"
import { isRoleAllowedReply } from "./utils"

interface FeedbackDrawerProps extends FormComponentProps {
  visible: boolean
  onClose(): void
  actionType: ActionType
  review: Review
}

export const FeedbackDrawerComponent: React.FC<FeedbackDrawerProps> = ({
  visible,
  onClose,
  actionType,
  review,
  form,
}: FeedbackDrawerProps) => {
  const showStatus =
    review.unreportableReason !==
    ReviewUnReportableReason.ReviewUnreportableReasonReportable
  let drawerTitle = ""
  let showReplyForm = false
  let showFooter = false
  let footerText = ""

  const dispatch = useDispatch()

  const textMap = useTranslationsMap({
    report: "feedback.cta.report",
    replyDrawerTitleAdd: "feedback.reply.drawer.title",
    submit: "feedback.reply.drawer.submit",
    replyDrawerTitleEdit: "feedback.reply.drawer.editTitle",
    done: "feedback.reply.drawer.done",
    submittedTitle: "feedback.toast.reply.submitted.title",
    submittedBody: "feedback.toast.reply.submitted.body",
    errorSubmittedTitle: "feedback.toast.reply.errorSubmitted.title",
    errorSubmittedBody: "feedback.toast.reply.errorSubmitted.body",
    editedTitle: "feedback.toast.reply.edited.title",
    editedBody: "feedback.toast.reply.edited.body",
    errorEditedTitle: "feedback.toast.reply.errorEdited.title",
    errorEditedBody: "feedback.toast.reply.errorEdited.body",
  })

  const { feedback } = useSelector((state: AppState) => state)

  switch (actionType) {
    case ActionType.ADD_REPLY:
      drawerTitle = textMap.replyDrawerTitleAdd
      showReplyForm = true
      showFooter = true
      footerText = textMap.submit
      break
    case ActionType.EDIT_REPLY:
      drawerTitle = textMap.replyDrawerTitleEdit
      showReplyForm = true
      showFooter = true
      footerText = textMap.done
      break
    default:
      drawerTitle = textMap.report // should be "Report Details"
      break
  }

  const handleReplySubmission = () => {
    form.validateFields((err, values) => {
      if (!err) {
        const replyID = review?.reviewReplies?.[0]?.replyID
        const description = values?.description

        if (isEmpty(replyID)) {
          dispatch(
            actionCreator.feedback.submitReplyMessage({
              body: {
                reviewID: review?.reviewID,
                description,
              },
              merchantID: review?.merchantID,
              form,
              textMap,
              review,
            }),
          )

          TroyWebTracker.sendEvent({
            category: EventCategory.feedbackReply,
            action: EventAction.clickSubmit,
            labels: {
              REVIEW_ID: review?.reviewID,
              REVIEW_RATING: review?.rating,
              REPLY_MESSAGE: description,
            },
          })
        } else if (replyID) {
          dispatch(
            actionCreator.feedback.editReplyMessage({
              body: {
                reviewID: review?.reviewID,
                replyID,
                description,
              },
              merchantID: review?.merchantID,
              form,
              textMap,
              review,
            }),
          )

          TroyWebTracker.sendEvent({
            category: EventCategory.feedbackReplyEdit,
            action: EventAction.clickSubmit,
            labels: {
              REVIEW_ID: review?.reviewID,
              REVIEW_RATING: review?.rating,
              REPLY_MESSAGE: description,
            },
          })
        }
      }
    })
  }

  useEffect(() => {
    const replyID = review?.reviewReplies?.[0]?.replyID
    if (isEmpty(replyID) && feedback.formError) {
      TroyWebTracker.sendEvent({
        category: EventCategory.feedbackReply,
        action: EventAction.errorSubmit,
        labels: {
          REVIEW_ID: review?.reviewID,
          REVIEW_RATING: review?.rating,
          ERROR_REASON: feedback.formError,
        },
      })
    }
  }, [review, feedback.formError])
  const allowReply = isRoleAllowedReply()

  // Temp reply cannot be edited yet until refreshed
  const isTempReply =
    review?.reviewReplies?.[0] && !review?.reviewReplies?.[0]?.replyID

  return (
    <Drawer
      destroyOnClose
      data-testid="feedback-drawer"
      open={visible}
      onClose={onClose}
      title={drawerTitle}
      width={475}
    >
      {showStatus && <DrawerReviewStatus review={review} />}

      <DrawerReviewDetails review={review} />

      <Form>
        {showReplyForm && (
          <DrawerReviewReplyForm
            data-testid="reply-form"
            review={review}
            form={form}
            allowReply={allowReply && !isTempReply}
          />
        )}

        {showFooter && (
          <div css={drawerFooter}>
            <Button
              data-testid="reply-submit-btn"
              htmlType="submit"
              type="primary"
              block
              style={{ fontWeight: 500, fontSize: 16 }}
              disabled={feedback.sending || !allowReply || isTempReply}
              onClick={e => {
                e.preventDefault()
                handleReplySubmission()
              }}
            >
              {footerText}
            </Button>
          </div>
        )}
      </Form>
    </Drawer>
  )
}

export const FeedbackDrawer = Form.create<FeedbackDrawerProps>({})(
  FeedbackDrawerComponent,
)
